// Analytics consent, shared with macswarm.com. The choice is one cookie on the registrable domain (`ms_consent`),
// written by either banner, the region comes from the Worker as <html data-consent> ('ask' in the EEA, the UK and
// Switzerland, 'implied' elsewhere), and Global Privacy Control counts as a no. The website's shared/consent.mjs
// and shared/analytics.js hold the same rules.

export type ConsentChoice = 'granted' | 'denied';

type ConsentInput = { stored: ConsentChoice | null; mode: string | undefined; gpc: boolean };

const ROOT_DOMAIN = 'macswarm.com';

export function consentFromCookie(cookie: string): ConsentChoice | null {
  const match = /(?:^|;\s*)ms_consent=(granted|denied)(?:;|$)/.exec(cookie);
  return match?.[1] === 'granted' || match?.[1] === 'denied' ? match[1] : null;
}

/** The cookie string for a choice; on macswarm.com and its subdomains it is shared by the website and the app. */
export function consentCookie(choice: ConsentChoice, hostname: string): string {
  const isShared = hostname === ROOT_DOMAIN || hostname.endsWith(`.${ROOT_DOMAIN}`);
  return `ms_consent=${choice}; Path=/; Max-Age=31536000; SameSite=Lax; Secure${isShared ? `; Domain=${ROOT_DOMAIN}` : ''}`;
}

/** A stored choice always wins; GPC is a no; 'ask' regions start without cookies until the banner is answered. */
export function initialConsent({ stored, mode, gpc }: ConsentInput): ConsentChoice {
  if (stored) return stored;
  if (gpc) return 'denied';
  return mode === 'implied' ? 'granted' : 'denied';
}

/** The banner shows only where the region asks first and nothing has answered yet (a choice or GPC). */
export function needsConsentChoice({ stored, mode, gpc }: ConsentInput): boolean {
  return !stored && !gpc && mode !== 'implied';
}

function readInput(): ConsentInput {
  const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
  return {
    stored: consentFromCookie(document.cookie),
    mode: document.documentElement.dataset.consent,
    gpc: gpc === true,
  };
}

export const readConsent = (): ConsentChoice => initialConsent(readInput());

export const shouldAskConsent = (): boolean => needsConsentChoice(readInput());
